<template>
  <div class="w-72 border-l border-slate-200 bg-white flex flex-col flex-shrink-0 overflow-hidden">
    <!-- 头部 -->
    <div class="h-14 px-4 flex items-center justify-between border-b border-slate-200 flex-shrink-0">
      <h4 class="text-sm font-semibold text-slate-700">
        {{ conversation.type === 'group' ? '群聊详情' : '联系人信息' }}
      </h4>
      <button
        class="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
        @click="chatStore.toggleInfoPanel()"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    </div>

    <!-- 内容区域 -->
    <div class="flex-1 overflow-y-auto">
      <!-- 单人聊天信息 -->
      <template v-if="conversation.type !== 'group'">
        <div class="p-6 text-center border-b border-slate-100">
          <div
            class="w-16 h-16 mx-auto rounded-full flex items-center justify-center text-xl font-medium text-white mb-3"
            :class="conversation.type === 'internal' ? 'bg-primary-500' : 'bg-emerald-500'"
          >
            {{ conversation.name.slice(-2) }}
          </div>
          <h3 class="text-base font-semibold text-slate-800">{{ conversation.name }}</h3>
          <span
            class="inline-block mt-1.5 px-2 py-0.5 text-xs rounded-full"
            :class="conversation.type === 'internal'
              ? 'bg-primary-100 text-primary-600'
              : 'bg-emerald-100 text-emerald-600'"
          >
            {{ conversation.type === 'internal' ? '内部人员' : 'C端客户' }}
          </span>
          <p v-if="conversation.online" class="text-xs text-green-500 mt-1.5">当前在线</p>
        </div>

        <!-- 详细信息 -->
        <div class="p-4 space-y-4">
          <!-- 内部人员额外信息 -->
          <template v-if="conversation.type === 'internal'">
            <div class="flex items-start gap-2">
              <span class="text-xs text-slate-400 w-14 flex-shrink-0 pt-0.5">部门</span>
              <span class="text-sm text-slate-700 break-all">{{ conversation.department || '-' }}</span>
            </div>
            <div class="flex items-start gap-2">
              <span class="text-xs text-slate-400 w-14 flex-shrink-0 pt-0.5">职位</span>
              <span class="text-sm text-slate-700 break-all">{{ conversation.position || '-' }}</span>
            </div>
          </template>

          <!-- C端客户额外信息 -->
          <template v-if="conversation.type === 'customer'">
            <div class="flex items-start gap-2">
              <span class="text-xs text-slate-400 w-14 flex-shrink-0 pt-0.5">来源</span>
              <span class="text-sm text-slate-700 break-all">{{ conversation.source || '-' }}</span>
            </div>
            <div class="flex items-start gap-2">
              <span class="text-xs text-slate-400 w-14 flex-shrink-0 pt-0.5">客户等级</span>
              <span class="text-sm text-slate-700 break-all">{{ conversation.customerLevel || '-' }}</span>
            </div>
          </template>

          <!-- 通用信息 -->
          <div class="flex items-start gap-2">
            <span class="text-xs text-slate-400 w-14 flex-shrink-0 pt-0.5">手机号</span>
            <span class="text-sm text-slate-700 break-all">{{ conversation.phone || '-' }}</span>
          </div>
          <div v-if="conversation.email" class="flex items-start gap-2">
            <span class="text-xs text-slate-400 w-14 flex-shrink-0 pt-0.5">邮箱</span>
            <span class="text-sm text-slate-700 break-all">{{ conversation.email }}</span>
          </div>
        </div>

        <!-- 快捷操作 -->
        <div class="p-4 border-t border-slate-100">
          <p class="text-xs text-slate-500 mb-3 font-medium">快捷操作</p>
          <div class="grid grid-cols-3 gap-2">
            <button class="flex flex-col items-center gap-1.5 p-2 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">
              <div class="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center">
                <svg class="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                </svg>
              </div>
              <span class="text-[10px] text-slate-500">电话</span>
            </button>
            <button class="flex flex-col items-center gap-1.5 p-2 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">
              <div class="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center">
                <svg class="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                </svg>
              </div>
              <span class="text-[10px] text-slate-500">视频</span>
            </button>
            <button class="flex flex-col items-center gap-1.5 p-2 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">
              <div class="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center">
                <svg class="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                </svg>
              </div>
              <span class="text-[10px] text-slate-500">主页</span>
            </button>
          </div>
        </div>
      </template>

      <!-- 群聊信息 -->
      <template v-else>
        <!-- 群头部信息 -->
        <div class="p-6 text-center border-b border-slate-100">
          <div class="w-16 h-16 mx-auto rounded-full bg-violet-500 flex items-center justify-center mb-3">
            <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
            </svg>
          </div>
          <!-- 群名称（群主/管理员可编辑） -->
          <div v-if="editingGroupName" class="flex items-center gap-1.5 justify-center mb-1">
            <input
              v-model="groupNameInput"
              class="text-base font-semibold text-slate-800 text-center border-b border-primary-400 outline-none bg-transparent w-36"
              @keydown.enter="saveGroupName"
              @keydown.escape="editingGroupName = false"
            />
            <button class="p-0.5 text-primary-500 hover:text-primary-600 cursor-pointer" @click="saveGroupName">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
            </button>
            <button class="p-0.5 text-slate-400 hover:text-slate-600 cursor-pointer" @click="editingGroupName = false">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
          </div>
          <div v-else class="flex items-center justify-center gap-1 mb-1">
            <h3 class="text-base font-semibold text-slate-800">{{ conversation.name }}</h3>
            <button
              v-if="isOwnerOrAdmin"
              class="p-0.5 text-slate-300 hover:text-slate-500 cursor-pointer"
              title="修改群名称"
              @click="startEditGroupName"
            >
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/></svg>
            </button>
          </div>
          <p class="text-xs text-slate-500">{{ conversation.memberCount }} 名成员</p>
          <!-- 群公告/描述 -->
          <div v-if="editingDescription" class="mt-2 px-2">
            <textarea
              v-model="descriptionInput"
              class="form-textarea text-xs leading-snug"
              rows="2"
              placeholder="输入群公告..."
            ></textarea>
            <div class="flex justify-end gap-1 mt-1">
              <button class="px-2 py-0.5 text-xs text-slate-500 hover:text-slate-700 cursor-pointer" @click="editingDescription = false">取消</button>
              <button class="px-2 py-0.5 text-xs text-white bg-primary-500 hover:bg-primary-600 rounded cursor-pointer" @click="saveDescription">保存</button>
            </div>
          </div>
          <div v-else class="mt-1.5 flex items-center justify-center gap-1">
            <p class="text-xs text-slate-400">{{ conversation.description || '暂无群公告' }}</p>
            <button
              v-if="isOwnerOrAdmin"
              class="p-0.5 text-slate-300 hover:text-slate-500 cursor-pointer"
              title="编辑群公告"
              @click="startEditDescription"
            >
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/></svg>
            </button>
          </div>
        </div>

        <!-- 群成员列表 -->
        <div class="p-4">
          <div class="flex items-center justify-between mb-3">
            <p class="text-xs text-slate-500 font-medium">群成员 ({{ conversation.members?.length || 0 }})</p>
          </div>

          <div class="space-y-0.5">
            <div
              v-for="member in conversation.members"
              :key="member.id"
              class="flex items-center gap-2.5 p-2 rounded-lg hover:bg-slate-50 transition-colors relative"
            >
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium text-white flex-shrink-0"
                :class="member.role === 'owner' ? 'bg-amber-500' : member.role === 'admin' ? 'bg-primary-500' : 'bg-slate-400'"
              >
                {{ member.name.slice(-1) }}
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-1.5">
                  <span class="text-sm text-slate-700 truncate">{{ member.name }}</span>
                  <span
                    v-if="member.role === 'owner'"
                    class="px-1 py-0.5 text-[10px] leading-none rounded bg-amber-100 text-amber-600 flex-shrink-0"
                  >群主</span>
                  <span
                    v-else-if="member.role === 'admin'"
                    class="px-1 py-0.5 text-[10px] leading-none rounded bg-blue-100 text-blue-600 flex-shrink-0"
                  >管理员</span>
                </div>
                <p class="text-[11px] text-slate-400">{{ member.department }}</p>
              </div>
              <!-- 成员操作按钮（仅群主/管理员可见，不可操作自己和群主） -->
              <button
                v-if="isOwnerOrAdmin && String(member.id) !== String(currentUserId) && member.role !== 'owner'"
                class="p-1 text-slate-300 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors cursor-pointer"
                @click.stop="openMemberMenu($event, member)"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- 添加成员按钮 -->
          <button
            v-if="isOwnerOrAdmin"
            class="mt-3 w-full flex items-center justify-center gap-1.5 py-2 border border-dashed border-slate-300 rounded-lg text-xs text-slate-500 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer"
            @click="showAddMemberPanel = true"
          >
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
            </svg>
            添加成员
          </button>
        </div>

        <!-- 群聊设置 -->
        <div class="p-4 border-t border-slate-100">
          <p class="text-xs text-slate-500 mb-3 font-medium">群聊设置</p>
          <div class="space-y-2">
            <div
              class="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 cursor-pointer"
              @click="chatStore.muteConversation(conversation.id)"
            >
              <span class="text-sm text-slate-600">消息免打扰</span>
              <div
                class="w-8 h-5 rounded-full relative cursor-pointer transition-colors"
                :class="chatStore.isConversationMuted(conversation.id) ? 'bg-primary-500' : 'bg-slate-200'"
              >
                <div
                  class="w-4 h-4 rounded-full bg-white shadow-sm absolute top-0.5 transition-transform"
                  :class="chatStore.isConversationMuted(conversation.id) ? 'translate-x-3' : 'translate-x-0.5'"
                ></div>
              </div>
            </div>
            <div
              class="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 cursor-pointer"
              @click="chatStore.pinConversation(conversation.id)"
            >
              <span class="text-sm text-slate-600">置顶聊天</span>
              <div
                class="w-8 h-5 rounded-full relative cursor-pointer transition-colors"
                :class="chatStore.isConversationPinned(conversation.id) ? 'bg-primary-500' : 'bg-slate-200'"
              >
                <div
                  class="w-4 h-4 rounded-full bg-white shadow-sm absolute top-0.5 transition-transform"
                  :class="chatStore.isConversationPinned(conversation.id) ? 'translate-x-3' : 'translate-x-0.5'"
                ></div>
              </div>
            </div>
          </div>
        </div>

      </template>
    </div>

    <!-- 底部操作区（群聊时固定在底部） -->
    <div v-if="conversation.type === 'group'" class="p-3 border-t border-slate-200 space-y-2 flex-shrink-0 bg-white">
      <!-- 转让群主（仅群主） -->
      <button
        v-if="chatStore.currentUserRole === 'owner'"
        class="w-full flex items-center justify-center gap-1.5 py-2 text-sm text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer"
        @click="showTransferPanel = true"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
        </svg>
        转让群主
      </button>
      <!-- 解散群聊（仅群主） -->
      <button
        v-if="chatStore.currentUserRole === 'owner'"
        class="w-full flex items-center justify-center gap-1.5 py-2 text-sm text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
        @click="handleDissolveGroup"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
        </svg>
        解散群聊
      </button>
      <!-- 退出群聊（非群主） -->
      <button
        v-if="chatStore.currentUserRole !== 'owner'"
        class="w-full flex items-center justify-center gap-1.5 py-2 text-sm text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
        @click="handleLeaveGroup"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
        </svg>
        退出群聊
      </button>
    </div>

    <!-- 成员操作菜单 -->
    <teleport to="body">
      <div v-if="memberMenuVisible" class="fixed inset-0 z-[100]" @click="memberMenuVisible = false" @contextmenu.prevent="memberMenuVisible = false">
        <div
          class="absolute bg-white rounded-lg shadow-lg border border-slate-200 py-1 min-w-[130px] z-[101]"
          :style="{ left: memberMenuPos.x + 'px', top: memberMenuPos.y + 'px' }"
        >
          <!-- 设为/取消管理员（仅群主可操作） -->
          <button
            v-if="chatStore.currentUserRole === 'owner' && activeMember?.role !== 'admin'"
            class="w-full px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 text-left flex items-center gap-2 cursor-pointer"
            @click="handleSetAdmin"
          >
            <svg class="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>
            设为管理员
          </button>
          <button
            v-if="chatStore.currentUserRole === 'owner' && activeMember?.role === 'admin'"
            class="w-full px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 text-left flex items-center gap-2 cursor-pointer"
            @click="handleCancelAdmin"
          >
            <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.618 5.984A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016zM12 9v2m0 4h.01"/></svg>
            取消管理员
          </button>
          <!-- 转让群主（仅群主可操作） -->
          <button
            v-if="chatStore.currentUserRole === 'owner'"
            class="w-full px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 text-left flex items-center gap-2 cursor-pointer"
            @click="handleTransferToMember"
          >
            <svg class="w-4 h-4 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/></svg>
            转让群主
          </button>
          <!-- 移出群聊 -->
          <button
            class="w-full px-3 py-1.5 text-sm text-red-500 hover:bg-red-50 text-left flex items-center gap-2 cursor-pointer"
            @click="handleRemoveMember"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM21 12h-6"/></svg>
            移出群聊
          </button>
        </div>
      </div>
    </teleport>

    <!-- 添加成员面板 -->
    <teleport to="body">
      <div v-if="showAddMemberPanel" class="fixed inset-0 z-50 flex items-center justify-center bg-black/30" @click.self="showAddMemberPanel = false">
        <div class="bg-white rounded-xl shadow-xl w-80 max-h-[70vh] flex flex-col overflow-hidden">
          <div class="px-4 py-3 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
            <h4 class="text-sm font-semibold text-slate-700">添加群成员</h4>
            <button class="p-1 text-slate-400 hover:text-slate-600 cursor-pointer" @click="showAddMemberPanel = false">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
          </div>
          <!-- 搜索 -->
          <div class="px-4 py-2 border-b border-slate-100 flex-shrink-0">
            <input
              v-model="addMemberSearch"
              class="form-search-input form-control-sm"
              placeholder="搜索联系人..."
            />
          </div>
          <!-- 已选成员 -->
          <div v-if="selectedNewMembers.length > 0" class="px-4 py-2 border-b border-slate-100 flex flex-wrap gap-1 flex-shrink-0">
            <span
              v-for="m in selectedNewMembers"
              :key="m.id"
              class="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-50 text-primary-600 text-xs rounded-full"
            >
              {{ m.name }}
              <button class="hover:text-primary-800 cursor-pointer" @click="toggleNewMember(m)">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </span>
          </div>
          <!-- 联系人列表 -->
          <div class="flex-1 overflow-y-auto">
            <div
              v-for="contact in addableContacts"
              :key="contact.id"
              class="flex items-center gap-2.5 px-4 py-2 hover:bg-slate-50 cursor-pointer transition-colors"
              @click="toggleNewMember(contact)"
            >
              <div
                class="w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors"
                :class="isNewMemberSelected(contact.id)
                  ? 'bg-primary-500 border-primary-500'
                  : 'border-slate-300'"
              >
                <svg v-if="isNewMemberSelected(contact.id)" class="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
              </div>
              <div class="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-medium text-white flex-shrink-0"
                :class="contact.type === 'internal' ? 'bg-primary-400' : 'bg-emerald-400'"
              >
                {{ contact.name.slice(-1) }}
              </div>
              <div class="min-w-0 flex-1">
                <p class="text-sm text-slate-700 truncate">{{ contact.name }}</p>
                <p class="text-[11px] text-slate-400 truncate">{{ contact.department || contact.source || '' }}</p>
              </div>
            </div>
            <p v-if="addableContacts.length === 0" class="py-6 text-center text-xs text-slate-400">没有可添加的联系人</p>
          </div>
          <!-- 确认 -->
          <div class="px-4 py-3 border-t border-slate-200 flex-shrink-0">
            <button
              class="w-full py-2 text-sm rounded-lg transition-colors cursor-pointer"
              :class="selectedNewMembers.length > 0
                ? 'bg-primary-500 text-white hover:bg-primary-600'
                : 'bg-slate-100 text-slate-400 cursor-not-allowed'"
              :disabled="selectedNewMembers.length === 0"
              @click="confirmAddMembers"
            >
              确认添加 ({{ selectedNewMembers.length }})
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- 转让群主面板 -->
    <teleport to="body">
      <div v-if="showTransferPanel" class="fixed inset-0 z-50 flex items-center justify-center bg-black/30" @click.self="showTransferPanel = false">
        <div class="bg-white rounded-xl shadow-xl w-80 max-h-[60vh] flex flex-col overflow-hidden">
          <div class="px-4 py-3 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
            <h4 class="text-sm font-semibold text-slate-700">转让群主</h4>
            <button class="p-1 text-slate-400 hover:text-slate-600 cursor-pointer" @click="showTransferPanel = false">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
          </div>
          <p class="px-4 py-2 text-xs text-slate-400 border-b border-slate-100 flex-shrink-0">选择一位成员作为新群主，转让后你将成为管理员</p>
          <div class="flex-1 overflow-y-auto">
            <div
              v-for="member in transferableMembers"
              :key="member.id"
              class="flex items-center gap-2.5 px-4 py-2.5 hover:bg-slate-50 cursor-pointer transition-colors"
              @click="handleTransferOwner(member)"
            >
              <div class="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium text-white flex-shrink-0"
                :class="member.role === 'admin' ? 'bg-primary-500' : 'bg-slate-400'"
              >
                {{ member.name.slice(-1) }}
              </div>
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-1.5">
                  <span class="text-sm text-slate-700">{{ member.name }}</span>
                  <span v-if="member.role === 'admin'" class="px-1 py-0.5 text-[10px] leading-none rounded bg-blue-100 text-blue-600">管理员</span>
                </div>
                <p class="text-[11px] text-slate-400">{{ member.department }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useChatStore } from '@/stores/chat'
import { useUserStore } from '@/stores/user'
import { ElMessage, ElMessageBox } from 'element-plus'

const chatStore = useChatStore()
const userStore = useUserStore()
const conversation = computed(() => chatStore.currentConversation)
const currentUserId = computed(() => userStore.userInfo?.userId)

const isOwnerOrAdmin = computed(() => {
  const role = chatStore.currentUserRole
  return role === 'owner' || role === 'admin'
})

// 群名称编辑
const editingGroupName = ref(false)
const groupNameInput = ref('')

function startEditGroupName() {
  groupNameInput.value = conversation.value.name
  editingGroupName.value = true
}

function saveGroupName() {
  const name = groupNameInput.value.trim()
  if (!name) return
  chatStore.updateGroupInfo(conversation.value.id, { name })
  editingGroupName.value = false
  ElMessage.success('群名称已修改')
}

// 群公告编辑
const editingDescription = ref(false)
const descriptionInput = ref('')

function startEditDescription() {
  descriptionInput.value = conversation.value.description || ''
  editingDescription.value = true
}

function saveDescription() {
  chatStore.updateGroupInfo(conversation.value.id, { description: descriptionInput.value.trim() })
  editingDescription.value = false
  ElMessage.success('群公告已更新')
}

// 成员操作菜单
const memberMenuVisible = ref(false)
const memberMenuPos = ref({ x: 0, y: 0 })
const activeMember = ref(null)

function openMemberMenu(e, member) {
  const menuWidth = 150
  const menuHeight = 140
  const x = Math.min(e.clientX, window.innerWidth - menuWidth)
  const y = Math.min(e.clientY, window.innerHeight - menuHeight)
  memberMenuPos.value = { x, y }
  activeMember.value = member
  memberMenuVisible.value = true
}

function handleSetAdmin() {
  chatStore.setMemberRole(conversation.value.id, activeMember.value.id, 'admin')
  memberMenuVisible.value = false
  ElMessage.success(`已将 ${activeMember.value.name} 设为管理员`)
}

function handleCancelAdmin() {
  chatStore.setMemberRole(conversation.value.id, activeMember.value.id, 'member')
  memberMenuVisible.value = false
  ElMessage.success(`已取消 ${activeMember.value.name} 的管理员身份`)
}

function handleRemoveMember() {
  const name = activeMember.value.name
  ElMessageBox.confirm(`确定将 ${name} 移出群聊吗？`, '移出成员', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(() => {
    chatStore.removeGroupMember(conversation.value.id, activeMember.value.id)
    memberMenuVisible.value = false
    ElMessage.success(`已将 ${name} 移出群聊`)
  }).catch(() => {})
}

function handleTransferToMember() {
  const member = activeMember.value
  ElMessageBox.confirm(`确定将群主转让给 ${member.name} 吗？转让后你将成为管理员。`, '转让群主', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(() => {
    chatStore.transferOwner(conversation.value.id, member.id)
    memberMenuVisible.value = false
    ElMessage.success(`群主已转让给 ${member.name}`)
  }).catch(() => {})
}

// 添加成员
const showAddMemberPanel = ref(false)
const addMemberSearch = ref('')
const selectedNewMembers = ref([])

const addableContacts = computed(() => {
  const existingIds = new Set((conversation.value?.members || []).map(m => m.id))
  let list = chatStore.availableContacts.filter(c => !existingIds.has(c.id))
  if (addMemberSearch.value) {
    const keyword = addMemberSearch.value.toLowerCase()
    list = list.filter(c => c.name.toLowerCase().includes(keyword))
  }
  return list
})

function isNewMemberSelected(id) {
  return selectedNewMembers.value.some(m => m.id === id)
}

function toggleNewMember(contact) {
  const idx = selectedNewMembers.value.findIndex(m => m.id === contact.id)
  if (idx > -1) {
    selectedNewMembers.value.splice(idx, 1)
  } else {
    selectedNewMembers.value.push(contact)
  }
}

function confirmAddMembers() {
  if (selectedNewMembers.value.length === 0) return
  chatStore.addGroupMembers(conversation.value.id, selectedNewMembers.value)
  ElMessage.success(`已添加 ${selectedNewMembers.value.length} 名成员`)
  selectedNewMembers.value = []
  addMemberSearch.value = ''
  showAddMemberPanel.value = false
}

// 转让群主面板
const showTransferPanel = ref(false)

const transferableMembers = computed(() => {
  return (conversation.value?.members || []).filter(m => String(m.id) !== String(currentUserId.value) && m.role !== 'owner')
})

function handleTransferOwner(member) {
  ElMessageBox.confirm(`确定将群主转让给 ${member.name} 吗？转让后你将成为管理员。`, '转让群主', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(() => {
    chatStore.transferOwner(conversation.value.id, member.id)
    showTransferPanel.value = false
    ElMessage.success(`群主已转让给 ${member.name}`)
  }).catch(() => {})
}

// 解散群聊
function handleDissolveGroup() {
  ElMessageBox.confirm('解散群聊后，所有群成员将被移出，聊天记录将被清除。确定解散吗？', '解散群聊', {
    confirmButtonText: '解散',
    cancelButtonText: '取消',
    type: 'warning',
    confirmButtonClass: 'el-button--danger'
  }).then(() => {
    chatStore.dissolveGroup(conversation.value.id)
    ElMessage.success('群聊已解散')
  }).catch(() => {})
}

// 退出群聊
function handleLeaveGroup() {
  ElMessageBox.confirm('退出群聊后将不再接收此群的消息，确定退出吗？', '退出群聊', {
    confirmButtonText: '退出',
    cancelButtonText: '取消',
    type: 'warning',
    confirmButtonClass: 'el-button--danger'
  }).then(() => {
    chatStore.leaveGroup(conversation.value.id)
    ElMessage.success('已退出群聊')
  }).catch(() => {})
}
</script>
